import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactEChartsCore from "echarts-for-react/lib/core";
import * as echarts from "echarts/core";
import { BarChart, LineChart, PieChart, ScatterChart } from "echarts/charts";
import {
	GridComponent,
	LegendComponent,
	TitleComponent,
	TooltipComponent,
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import { buildEChartsOption } from "../utils/chartOptionBuilder";
import { Download } from "lucide-react";

// Tree-shaken registration
echarts.use([
	BarChart,
	LineChart,
	PieChart,
	ScatterChart,
	GridComponent,
	LegendComponent,
	TitleComponent,
	TooltipComponent,
	CanvasRenderer,
]);

const SUPPORTED_VERSION = 1;

interface EChartsBlockProps {
	chartData: Record<string, unknown>;
	chartType: string;
	title?: string;
	version: number;
	chartId?: string;
}

const EChartsBlock: React.FC<EChartsBlockProps> = ({
	chartData,
	chartType,
	title,
	version,
	chartId,
}) => {
	const chartRef = useRef<ReactEChartsCore>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const [isVisible, setIsVisible] = useState(false);
	const [hasError, setHasError] = useState(false);

	// IntersectionObserver: mount/unmount offscreen instances
	useEffect(() => {
		const el = containerRef.current;
		if (!el) return;
		const observer = new IntersectionObserver(
			([entry]) => setIsVisible(entry.isIntersecting),
			{ rootMargin: "200px" },
		);
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	const option = useMemo(() => {
		try {
			return buildEChartsOption(chartType, chartData, title);
		} catch {
			return null;
		}
	}, [chartType, chartData, title]);

	useEffect(() => {
		if (option === null) setHasError(true);
	}, [option]);

	const handleDownload = useCallback(() => {
		const instance = chartRef.current?.getEchartsInstance();
		if (!instance) return;
		const url = instance.getDataURL({ type: "png", pixelRatio: 2, backgroundColor: "#fff" });
		const a = document.createElement("a");
		a.href = url;
		a.download = `${title ?? "chart"}.png`;
		a.click();
	}, [title]);

	if (version > SUPPORTED_VERSION) {
		return (
			<div className="rounded-lg border border-amber-200 bg-amber-50 p-4 my-3 text-sm text-amber-800">
				Chart version {version} is not supported by this client. Please update to view this chart.
			</div>
		);
	}

	if (hasError) {
		return (
			<div className="rounded-lg border border-red-200 bg-red-50 p-4 my-3 text-sm text-red-700">
				Failed to render chart. The chart data may be malformed.
			</div>
		);
	}

	const height = chartType === "pie" || chartType === "scatter" ? 350 : 300;

	return (
		<div
			ref={containerRef}
			data-chart-block={chartId ?? ""}
			className="rounded-lg border border-gray-200 bg-white p-4 my-3 relative group"
		>
			{isVisible && option && (
				<>
					<ReactEChartsCore
						ref={chartRef}
						echarts={echarts}
						option={option}
						style={{ width: "100%", height }}
						notMerge
						lazyUpdate
						onChartReady={() => {}}
						onEvents={{}}
					/>
					<button
						onClick={handleDownload}
						className="absolute top-2 right-2 p-1.5 rounded-md bg-white/80 border border-gray-200 text-gray-500 hover:text-gray-700 hover:bg-white opacity-0 group-hover:opacity-100 transition-opacity"
						title="Download chart as PNG"
					>
						<Download className="w-4 h-4" />
					</button>
				</>
			)}
			{!isVisible && (
				<div style={{ height }} className="bg-gray-50 rounded animate-pulse" />
			)}
		</div>
	);
};

export default EChartsBlock;
